import { DeserializedSavedFilter, SavedFilter, SerializableFilterGroup } from 'api'
import { OrgUsersById } from 'context/orgUsersContext'
import {
  activityTypeToString,
  ContactType,
  contactTypeToString
} from 'modules/accountActivity/domain'
import {
  AccountType,
  ALL_ACTIVITY_TYPES,
  AttributeFilter,
  ContextFilter,
  FilterableEntity,
  FilterGroup,
  FilterOperator,
  NUMBER_OPERATOR_TYPES
} from 'utils/filters'

const CURRENT_USER_MARKER = '<userId>'
interface FilterOption {
  value: any
  label: string
}

export const transformFiltersToFrontend = (
  savedFilterSets: SavedFilter[],
  orgUsersById: OrgUsersById
): DeserializedSavedFilter[] => {
  const userOptions = Object.entries(orgUsersById)
    .filter(([, user]) => user?.status === 'ENABLED' && !user.bot && (user.name || user.email))
    .map(([id, user]) => ({ value: id, label: user?.name || user?.email || '' }))
    .concat({ value: CURRENT_USER_MARKER, label: 'Current User' })

  return savedFilterSets.map(savedFilter => {
    const transformedFilterGroups = savedFilter.body.filterGroups.map(group =>
      tranformFilterGroupToFrontend(group, userOptions)
    )

    return {
      ...savedFilter,
      body: {
        ...savedFilter.body,
        filterGroups: transformedFilterGroups
      }
    }
  })
}

export const tranformFilterGroupToFrontend = (
  backendFilter: SerializableFilterGroup,
  userOptions: FilterOption[]
): FilterGroup => {
  const filterGroup: FilterGroup = []

  if (backendFilter.accountOwnerFilter) {
    const accountOwnerFilter = backendFilter.accountOwnerFilter
    const valuesToFilter = userOptions.filter(userOption =>
      accountOwnerFilter.values.includes(userOption.value)
    )

    filterGroup.push({
      filterType: FilterableEntity.ACCOUNT_OWNERS,
      operator: accountOwnerFilter.filterOperation as FilterOperator,
      values: valuesToFilter
    })
  }

  if (
    !!backendFilter.activityTypes &&
    backendFilter.activityTypes.length !== ALL_ACTIVITY_TYPES.size
  ) {
    filterGroup.push({
      filterType: FilterableEntity.ACTIVITIES,
      operator: FilterOperator.EQUALS,
      values: backendFilter.activityTypes.map(v => ({ label: activityTypeToString(v), value: v }))
    })
  }

  if (!!backendFilter.contactFilterType && backendFilter.contactFilterType !== ContactType.ALL) {
    filterGroup.push({
      filterType: FilterableEntity.CONTACT_TYPES,
      operator: FilterOperator.EQUALS,
      values: [
        {
          label: contactTypeToString(backendFilter.contactFilterType),
          value: backendFilter.contactFilterType
        }
      ]
    })
  }

  if (backendFilter.contactRoleFilter) {
    const contactRoleFilter = backendFilter.contactRoleFilter
    filterGroup.push({
      filterType: FilterableEntity.CONTACT_ROLE,
      operator: contactRoleFilter.filterOperation as FilterOperator,
      values: contactRoleFilter.values.map(v => ({ label: v, value: v }))
    })
  }

  if (backendFilter.targetedOnly) {
    filterGroup.push({
      filterType: FilterableEntity.TARGETED,
      operator: FilterOperator.EQUALS,
      values: { label: 'Targeted', value: AccountType.TARGETED }
    })
  }

  if (
    !!backendFilter.contexts &&
    !!backendFilter.contexts['PAGE_VIEW'] &&
    backendFilter.contexts['PAGE_VIEW'].length > 0
  ) {
    const contextFilter: ContextFilter = backendFilter.contexts['PAGE_VIEW'][0]
    filterGroup.push({
      filterType: FilterableEntity.PAGE_URL,
      operator: contextFilter.filterOperation as FilterOperator,
      values: contextFilter.values.map(v => ({ label: v, value: v }))
    })
  }

  if (!!backendFilter.attributeFilters && backendFilter.attributeFilters.length > 0) {
    return filterGroup.concat(
      transformCustomAttributeFilter(backendFilter.attributeFilters, userOptions)
    )
  }

  return filterGroup
}

const transformCustomAttributeFilter = (
  attributeFilters: AttributeFilter[],
  userOptions: FilterOption[]
): FilterGroup => {
  return attributeFilters.map(filter => {
    const operator = filter.filterOperation as FilterOperator
    const accountOwnerIds = userOptions.map(v => v.value)
    const type = getCustomAttributeType(filter.values, operator, accountOwnerIds)
    const values = getCustomAttributeValues(filter.values, type, userOptions)

    return {
      filterSubtype: filter.attribute,
      attributeType: type,
      filterType: FilterableEntity.CUSTOM_ATTRIBUTE,
      operator: operator,
      values: values
    }
  })
}

const getCustomAttributeValues = (
  filterValues: string[],
  type: string,
  userOptions: FilterOption[]
) => {
  if (type === 'TEAMMEMBER') {
    return userOptions.filter(userOption => filterValues.includes(userOption.value))
  } else if (type === 'NUMBER') {
    return filterValues[0]
  } else {
    return filterValues.map(value => ({ label: value, value: value }))
  }
}

const getCustomAttributeType = (
  filterValues: string[],
  operator: FilterOperator,
  accountOwnerIds: any[]
): string => {
  if (filterValues.every(value => accountOwnerIds.includes(value))) {
    return 'TEAMMEMBER'
  } else if (
    operator in NUMBER_OPERATOR_TYPES ||
    (filterValues.length === 1 && typeof filterValues[0] === 'number')
  ) {
    return 'NUMBER'
  }

  return null as any
}
